/**
 * How the relationship board says things: stage and health presentation, the
 * type chips, the line of figures on a card, and the "recent" list the peek
 * reads straight off the contact record.
 *
 * What is NOT here: any threshold or business rule. A stage, a score and a
 * smart list are decided on the server (`crm_relationships.py`) and arrive on
 * the row; this file only decides how a decided thing looks.
 */
import type { CrmContact } from '@/services/crm'
import type {
  HealthBand,
  RelationshipRow,
  RelationshipStage,
  SmartListKey,
} from '@/services/crmRelationships'
import { formatUsd } from '@/utils/money'
import { parseDateValue } from '@/utils/dates'
import { distinctCommunications } from '../crmFields'

/** Board columns, left to right. */
export const STAGE_ORDER: readonly RelationshipStage[] = ['core', 'active', 'cooling', 'new']

export interface StageStyle {
  title: string
  /** The small dot beside the label. Colour is never the only cue: the label
   *  is always printed next to it. */
  dot: string
  text: string
}

export const STAGE_STYLE: Record<RelationshipStage, StageStyle> = {
  core: { title: 'Core', dot: 'bg-amber-400', text: 'text-amber-300' },
  active: { title: 'Active', dot: 'bg-emerald-400', text: 'text-emerald-300' },
  cooling: { title: 'Cooling', dot: 'bg-orange-400', text: 'text-orange-300' },
  new: { title: 'New', dot: 'bg-sky-400', text: 'text-sky-300' },
}

export const BAND_STYLE: Record<HealthBand, { stroke: string; text: string; word: string }> = {
  strong: { stroke: '#34d399', text: 'text-emerald-300', word: 'Strong' },
  fair: { stroke: '#d9b768', text: 'text-amber-300', word: 'Fair' },
  weak: { stroke: '#fb923c', text: 'text-orange-300', word: 'Weak' },
}

/** The dot colour of each smart list in the rail. */
export const SMART_LIST_DOT: Record<SmartListKey, string> = {
  desk: 'bg-stone-300',
  core: STAGE_STYLE.core.dot,
  active: STAGE_STYLE.active.dot,
  cooling: STAGE_STYLE.cooling.dot,
  new: STAGE_STYLE.new.dot,
  confirm: 'bg-amber-200',
}

export type TypeChipKey = 'all' | 'broker' | 'sponsor' | 'investor'

export const TYPE_CHIPS: { key: TypeChipKey; label: string; companyType: CrmContact['companyType'] | null }[] = [
  { key: 'all', label: 'All', companyType: null },
  { key: 'broker', label: 'Brokers', companyType: 'Broker' },
  { key: 'sponsor', label: 'Sponsors', companyType: 'Borrower/Sponsor' },
  { key: 'investor', label: 'Investors', companyType: 'Investor' },
]

export function matchesTypeChip(contact: Pick<CrmContact, 'companyType'>, chip: TypeChipKey): boolean {
  const entry = TYPE_CHIPS.find((c) => c.key === chip)
  return !entry || entry.companyType === null || contact.companyType === entry.companyType
}

export function rowsById(rows: readonly RelationshipRow[] | undefined): Map<string, RelationshipRow> {
  return new Map((rows ?? []).map((row) => [row.id, row]))
}

/** The ring's value: a score, "NEW" for a contact too new to score, or a dash. */
export function healthLabel(row: Pick<RelationshipRow, 'health' | 'stage'> | undefined): string {
  if (!row) return '—'
  if (row.health !== null) return String(row.health)
  return row.stage === 'new' ? 'NEW' : '—'
}

/** What a screen reader and a tooltip say about the ring. */
export function healthDescription(row: Pick<RelationshipRow, 'health' | 'healthBand' | 'stage'> | undefined): string {
  if (!row) return 'No relationship data'
  if (row.health !== null) {
    const band = row.healthBand ? ` (${BAND_STYLE[row.healthBand].word.toLowerCase()})` : ''
    return `Relationship health ${row.health} of 100${band}`
  }
  if (row.stage === 'new') return 'New contact: no score yet'
  return 'No score yet: no dated deal or message on record'
}

/** Cards in a column: best health first, then most recent touch, then name. */
export function compareCards(
  a: { contact: CrmContact; row: RelationshipRow },
  b: { contact: CrmContact; row: RelationshipRow },
  stage: RelationshipStage,
): number {
  const touch = (r: RelationshipRow) => (r.lastTouch ? (parseDateValue(r.lastTouch)?.getTime() ?? 0) : 0)
  if (stage === 'new') {
    // New contacts have no score; the freshest arrival leads.
    return touch(b.row) - touch(a.row) || a.contact.displayName.localeCompare(b.contact.displayName)
  }
  const ha = a.row.health ?? -1
  const hb = b.row.health ?? -1
  return hb - ha || touch(b.row) - touch(a.row) || a.contact.displayName.localeCompare(b.contact.displayName)
}

/** "5 deals · $38.0M" - the volume only where an amount is on record. */
export function dealsLine(row: RelationshipRow | undefined, contact: Pick<CrmContact, 'linkedDeals'>): string {
  const deals = row ? row.deals : contact.linkedDeals.length
  const noun = deals === 1 ? 'deal' : 'deals'
  if (!row || row.volume === null) return `${deals} ${noun}`
  return `${deals} ${noun} · ${formatUsd(row.volume)}`
}

/** Where a volume comes from, for a tooltip: it covers only priced deals. */
export function volumeCoverage(row: Pick<RelationshipRow, 'deals' | 'dealsWithAmount'>): string {
  if (row.deals === 0) return 'No deals'
  return `Amount on ${row.dealsWithAmount} of ${row.deals} deals`
}

export interface RecentItem {
  key: string
  when: string
  label: string
}

/**
 * The newest dated things on the contact's record: messages and deal
 * appearances, one line each. A message and the deal it became share an
 * instant, so they are one line rather than two.
 */
export function recentItems(contact: CrmContact, limit = 5): RecentItem[] {
  const byInstant = new Map<number, { when: string; parts: string[]; key: string }>()
  const add = (when: string | null | undefined, part: string, key: string) => {
    const moment = parseDateValue(when ?? null)
    if (!moment || !when) return
    const at = moment.getTime()
    const existing = byInstant.get(at)
    if (existing) existing.parts.push(part)
    else byInstant.set(at, { when, parts: [part], key })
  }

  for (const comm of distinctCommunications(contact.communications ?? [])) {
    const label = comm.subject?.trim() || 'No subject'
    add(comm.timestamp, `${comm.type || 'Message'}: ${label}`, `m-${comm.id}`)
  }
  for (const deal of contact.dealHistory ?? []) {
    const amount = typeof deal.amount === 'number' && deal.amount > 0 ? `, ${formatUsd(deal.amount)}` : ''
    const stage = deal.stage ? ` · ${deal.stage}` : ''
    add(deal.date, `Deal: ${deal.property?.trim() || deal.dealId}${amount}${stage}`, `d-${deal.dealId}`)
  }

  return [...byInstant.entries()]
    .sort((a, b) => b[0] - a[0])
    .slice(0, limit)
    .map(([, entry]) => ({ key: entry.key, when: entry.when, label: entry.parts.join(' → ') }))
}
